import React, { useState } from "react";
import { Box } from "@mui/material";
import ChatPanel from "../ChatPanel/ChatPanel";
import SourcesPanel from "../SourcesPanel/SourcesPanel";
import TallyFormPanel from "../TallyFormPanel/TallyFormPanel";
import { ChatMode, ChatModeValues } from "../../types/frontend";
import { getStarterPrompts } from "../../hooks/useStarterPrompts";
import {
  DOCUMENTATION_URL,
  MATERIOM_AI_V2_DOCUMENTATION_URL,
} from "../../constants";

/**
 * Props for the MateriomAIContainer component.
 * @property {string} input - The current input value for the chat or form.
 * @property {(val: string) => void} setInput - Function to update the input value.
 * @property {ChatMode} [mode] - Optional mode for controlling the behavior of the chat (defaults to Workflow mode).
 */
interface MateriomAIContainerProps {
  input: string;
  setInput: (val: string) => void;
  mode?: ChatMode;
}

/**
 * MateriomAIContainer is the main container component for routing between different
 * workflow and agent chat modes, handling the input state, and organizing the layout
 * of the chat and sources panel in the Materiom AI frontend.
 *
 * @param {MateriomAIContainerProps} props - Props including input, setInput, and optional mode.
 * @returns {JSX.Element} The MateriomAIContainer layout and logic.
 */
const MateriomAIContainer: React.FC<MateriomAIContainerProps> = ({
  input,
  setInput,
  mode = ChatModeValues.Workflow,
}) => {
  // State to control the expansion of the sources panel
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);

  // Collect agent mode
  const isAgentMode = mode === ChatModeValues.Agent;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "auto",
      }}
    >
      {/* Main Content */}
      <Box
        sx={{
          display: "flex",
          height: "auto",
          overflow: "hidden",
          justifyContent: "center",
          width: "100%",
        }}
      >
        {/* Sources Panel Container (includes TallyFormPanel only in workflow mode) */}
        <Box
          sx={{
            width: {
              lg: isPanelExpanded ? "60%" : "45%",
              xl: isPanelExpanded ? "60%" : "45%",
            },
            minWidth: {
              sm: "300px",
              md: "350px",
              lg: "400px",
              xl: "450px",
            },
            display: { xs: "none", sm: "flex" },
            flexDirection: "column",
            mx: 2,
            gap: 2,
            height: { xs: "100vh", sm: "85vh" },
            transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
          }}
        >
          <SourcesPanel
            isPanelExpanded={isAgentMode ? false : isPanelExpanded}
            setIsPanelExpanded={isAgentMode ? () => {} : setIsPanelExpanded}
            hideToggle={isAgentMode}
            emptyStateMessage={
              isAgentMode
                ? "Start a chat. Sources will appear here."
                : undefined
            }
            enableAttachSources={!isAgentMode}
          />
          {/* TallyFormPanel - only shown in workflow mode */}
          {!isAgentMode && <TallyFormPanel />}
        </Box>
        {/* Chat Panel Container */}
        <Box
          sx={{
            flex: { xs: 3, lg: isPanelExpanded ? 2 : 3 }, // Reduce when expanded only on lg+
            minWidth: {
              xs: "100%", // Take full width on extra small screens
              sm: "300px", // More reasonable min-width for small screens
              md: "350px", // Increased min-width for medium screens
              lg: "400px", // Increased min-width for large screens
              xl: "450px", // Increased min-width for extra large screens
            },
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
          }}
        >
          <ChatPanel
            input={input}
            setInput={setInput}
            mode={mode} // TODO: remove  `mode` prop from ChatPanel
            title={"Chat"}
            starterTitle={isAgentMode ? "Materiom AI v2" : undefined}
            starterSubtitle={
              isAgentMode
                ? "An agentic AI tool for materials discovery."
                : undefined
            }
            starterPrompts={getStarterPrompts(mode)}
            documentationUrl={
              isAgentMode ? MATERIOM_AI_V2_DOCUMENTATION_URL : DOCUMENTATION_URL
            }
          />
        </Box>
      </Box>
    </Box>
  );
};

export default MateriomAIContainer;
